import { useState } from "react";
import { Search, Plus } from "lucide-react";
import type { Article, Category } from "@core/features/support";
import { Button } from "@themes/pollinator/ui/Button";
import { Loader } from "@themes/pollinator/ui/Loader";
import { Sheet } from "@themes/pollinator/ui/Sheet";
import { Type } from "@themes/pollinator/ui/Type";
import {
  SupportCategoryAccordionButton,
  SupportCategoryAccordion,
  SupportArticle,
} from "@platform/features/support";

export type Props = {
  article: Article;
  categories: Category[];
  isOpen: boolean;
  onClose: () => void;
  prev: Article | null;
  next: Article | null;
  setSheetArticleId: (articleId: string) => void;
  onSearchClick: () => void;
  onTicketClick: () => void;
};

export function SupportArticleSheet({
  article,
  categories,
  isOpen,
  onClose,
  prev,
  next,
  setSheetArticleId,
  onSearchClick,
  onTicketClick,
}: Props) {
  const [isCategoriesPanelOpen, setIsCategoriesPanelOpen] = useState(false);

  const handleNavigationClick = (article: Article) => {
    setSheetArticleId(article.id);
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-lg overflow-auto"
      innerClassName="justify-start"
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-between border-b p-4 h-16 mt-16 md:mt-0">
          <div className="flex items-center gap-2">
            <SupportCategoryAccordionButton
              onClick={() => setIsCategoriesPanelOpen(!isCategoriesPanelOpen)}
            />
            <Button
              variant="secondary"
              iconComponent={Search}
              onClick={onSearchClick}
              data-testid="support-article-search-open-modal"
            >
              Search
            </Button>
            <Button
              variant="secondary"
              iconComponent={Plus}
              onClick={onTicketClick}
              data-testid="support-article-open-ticket-modal"
            >
              Get help
            </Button>
          </div>
        </div>

        <SupportCategoryAccordion
          article={article}
          categories={categories}
          isOpen={isCategoriesPanelOpen}
          setIsOpen={setIsCategoriesPanelOpen}
          onNavigationClick={handleNavigationClick}
        />
      </div>
      <div className="flex-1 flex flex-col gap-8 px-4 py-8">
        {article ? (
          <>
            <Type as="h3" variant="h3">
              {article.title}
            </Type>
            <SupportArticle
              article={article}
              prev={prev}
              next={next}
              onNavigationClick={handleNavigationClick}
            />
          </>
        ) : (
          <Loader isFullPage />
        )}
      </div>
    </Sheet>
  );
}

export default SupportArticleSheet;
